<template>
    <div class="task-recurrence-editor">
        <strong>{{ $t("RecurringTask.title") }}</strong>

        <select v-model="frequency" class="field">
            <option value="day">{{ $t("RecurringTask.daily") }}</option>
            <option value="week">{{ $t("RecurringTask.weekly") }}</option>
            <option value="month">{{ $t("RecurringTask.monthly") }}</option>
            <option value="year">{{ $t("RecurringTask.yearly") }}</option>
            <option value="custom">{{ $t("RecurringTask.custom") }}</option>
        </select>

        <div v-if="frequency === 'custom'" class="every-row">
            <span>{{ $t("RecurringTask.every") }}</span>
            <input v-model.number="form.interval" type="number" min="1" max="30" class="field" />
            <select v-model="form.unit" class="field">
                <option value="day">{{ $t("RecurringTask.day") }}</option>
                <option value="week">{{ $t("RecurringTask.week") }}</option>
                <option value="month">{{ $t("RecurringTask.month") }}</option>
                <option value="year">{{ $t("RecurringTask.year") }}</option>
            </select>
        </div>

        <select v-model="form.trigger" class="field">
            <option value="schedule">{{ $t("RecurringTask.on_schedule") }}</option>
            <option value="complete">{{ $t("RecurringTask.on_status_complete") }}</option>
        </select>

        <label class="check">
            <input v-model="form.createNew" type="checkbox" />
            <span>{{ $t("RecurringTask.create_new_task") }}</span>
        </label>

        <label class="check">
            <input v-model="form.forever" type="checkbox" />
            <span>{{ $t("RecurringTask.recur_forever") }}</span>
        </label>
        <div v-if="!form.forever" class="until-row">
            <span>{{ $t("RecurringTask.until") }}</span>
            <input v-model="form.until" type="date" :min="minDate" class="field" />
        </div>

        <label class="check" :title="statusRequired ? $t('RecurringTask.status_required_when_reusing') : ''">
            <input v-model="form.updateStatus" type="checkbox" :disabled="statusRequired" />
            <span>{{ $t("RecurringTask.update_status_to") }}</span>
        </label>
        <select v-model="form.statusKey" class="field" :disabled="!form.updateStatus">
            <option v-for="status in selectableStatuses" :key="status.key" :value="status.key">{{ status.name }}</option>
        </select>

        <label v-if="form.trigger === 'complete'" class="check">
            <input v-model="form.syncToDueDate" type="checkbox" />
            <span>{{ $t("RecurringTask.sync_to_due_date") }}</span>
        </label>

        <div class="task-recurrence-actions">
            <button v-if="active" type="button" class="remove" :disabled="saving" @click="$emit('remove')">
                {{ $t("RecurringTask.remove") }}
            </button>
            <button type="button" class="cancel" :disabled="saving" @click="$emit('close')">
                {{ $t("Projects.cancel") }}
            </button>
            <button type="button" class="save" :disabled="saving || disabled" @click="$emit('save')">
                {{ $t("Projects.save") }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import recurrenceHelper from "@/utils/taskRecurrence.cjs";

const { DONE_STATUS_TYPES } = recurrenceHelper;

const props = defineProps({
    modelValue: { type: Object, required: true },
    statuses: { type: Array, default: () => [] },
    minDate: { type: String, default: "" },
    active: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "save", "remove", "close"]);
const form = computed({
    get: () => props.modelValue,
    set: (value) => emit("update:modelValue", value),
});

// On completion the next occurrence must not start out done, or it can never be completed again
const selectableStatuses = computed(() => (form.value.trigger === "complete"
    ? props.statuses.filter((status) => !DONE_STATUS_TYPES.includes(String(status.type).toLowerCase()))
    : props.statuses));
// Reusing the task on completion only recurs if it is reopened, so a status is required
const statusRequired = computed(() => form.value.trigger === "complete" && !form.value.createNew);

watch(statusRequired, (required) => {
    if (required) form.value.updateStatus = true;
}, { immediate: true });
watch(selectableStatuses, (list) => {
    if (list.length && !list.some((status) => status.key === form.value.statusKey)) form.value.statusKey = list[0].key;
}, { immediate: true });

// Daily/Weekly/Monthly/Yearly are "every 1 <unit>"; anything else is Custom
const isPreset = (value) => Number(value.interval) === 1 && ["day", "week", "month", "year"].includes(value.unit);
const customMode = ref(!isPreset(props.modelValue));
watch(() => props.modelValue, (value) => { customMode.value = !isPreset(value); });

const frequency = computed({
    get: () => (customMode.value ? "custom" : form.value.unit),
    set: (value) => {
        customMode.value = value === "custom";
        if (value !== "custom") {
            form.value.interval = 1;
            form.value.unit = value;
        }
    },
});
</script>

<style scoped>
.task-recurrence-editor { display: flex; flex-direction: column; gap: 10px; width: 100%; padding: 14px; background: #fff; text-align: left; }
.task-recurrence-editor strong { color: #1f2d3d; font-size: 14px; }
.task-recurrence-editor span { height: auto; padding: 0; font-size: inherit; line-height: normal; }
.task-recurrence-editor .field { width: 100%; min-width: 0; height: 34px; padding: 0 8px; border: 1px solid #ccd8e5; border-radius: 6px; background: #fff; color: #172b4d; font-size: 13px; }
.task-recurrence-editor .field:disabled { background: #f5f7fa; color: #8c98a7; }
.task-recurrence-editor .every-row { display: grid; grid-template-columns: auto 64px 1fr; align-items: center; gap: 7px; color: #526174; font-size: 12px; }
.task-recurrence-editor .until-row { display: grid; grid-template-columns: auto 1fr; align-items: center; gap: 7px; padding-left: 24px; color: #526174; font-size: 12px; }
.task-recurrence-editor .check { display: flex; align-items: center; gap: 8px; margin: 0; color: #172b4d; font-size: 13px; cursor: pointer; }
.task-recurrence-editor .check input { width: 15px; height: 15px; margin: 0; accent-color: #0875de; cursor: pointer; }
.task-recurrence-editor .check input:disabled { cursor: not-allowed; }
.task-recurrence-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 4px; }
.task-recurrence-actions button { min-height: 32px; padding: 0 12px; border-radius: 6px; cursor: pointer; }
.task-recurrence-actions button:disabled { cursor: not-allowed; opacity: .55; }
.task-recurrence-actions .save { border: 1px solid #0875de; background: #0875de; color: #fff; }
.task-recurrence-actions .cancel { border: 1px solid #ccd8e5; background: #fff; color: #526174; }
.task-recurrence-actions .remove { margin-right: auto; border: 0; background: transparent; color: #d92d20; }
</style>
